import React, { useState, useEffect } from 'react';
import { getATSResumes, ATSResume } from '../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Grid,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Divider,
  SelectChangeEvent,
  Skeleton
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import './ResumeHistory.css';

const ResumeHistorySkeleton: React.FC = () => (
  <Grid container spacing={3}>
    {[1, 2, 3].map((index) => (
      <Grid item xs={12} md={6} lg={4} key={index}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width="60%" height={32} />
              </Stack>

              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="text" width="80%" height={24} />
                </Stack>

                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Skeleton variant="circular" width={20} height={20} />
                    <Skeleton variant="text" width="40%" height={24} />
                  </Stack>
                  <Skeleton variant="rectangular" height={8} />
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="text" width="50%" height={24} />
                </Stack>
              </Stack>
            </Stack>
          </CardContent>

          <Divider />

          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={36} />
          </Box>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const ResumeHistory: React.FC = () => {
  const [resumes, setResumes] = useState<ATSResume[]>([]);
  const [filteredResumes, setFilteredResumes] = useState<ATSResume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'week', 'month', 'year'
  const [showFilters, setShowFilters] = useState(false);

  const fetchResumes = async (search?: string, date?: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getATSResumes(search, date);
      setResumes(data);
      setFilteredResumes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch resume history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResumes();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleDateFilterChange = (e: SelectChangeEvent) => {
    setDateFilter(e.target.value);
  };

  const handleSearch = () => {
    fetchResumes(searchTerm, dateFilter);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateFilter('all');
    fetchResumes();
  };

  const handleDownload = (url: string, companyName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${companyName}_optimized_resume.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Card elevation={0} sx={{ 
          width: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
          overflow: 'hidden',
          mb: 3
        }}>
          <CardContent sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
              <HistoryIcon color="primary" />
              <Typography variant="h5" fontWeight="600">
                Resume History
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Stack direction="row" spacing={1}>
                <Skeleton variant="rectangular" width={120} height={36} />
                <Skeleton variant="rectangular" width={100} height={36} />
              </Stack>
            </Stack>

            <ResumeHistorySkeleton />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
        <Alert 
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => fetchResumes()}
              startIcon={<RefreshIcon />}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Card elevation={0} sx={{ 
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        overflow: 'hidden',
        mb: 3
      }}>
        <CardContent sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h5" fontWeight="600">
              Resume History
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterListIcon />}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => fetchResumes()}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          {showFilters && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search by company or job title..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchTerm('')}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Time Period</InputLabel>
                    <Select
                      value={dateFilter}
                      onChange={handleDateFilterChange}
                      label="Time Period"
                      startAdornment={
                        <InputAdornment position="start">
                          <CalendarIcon color="action" />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="week">Last Week</MenuItem>
                      <MenuItem value="month">Last Month</MenuItem>
                      <MenuItem value="year">Last Year</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSearch}
                      startIcon={<SearchIcon />}
                    >
                      Search
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          )}

          {filteredResumes.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <Typography color="text.secondary">
                {resumes.length === 0 
                  ? "No resume history found. Generate your first ATS-optimized resume!"
                  : "No resumes match your search criteria. Try adjusting your filters."}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredResumes.map((resume) => (
                <Grid item xs={12} md={6} lg={4} key={resume.id}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 1
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <BusinessIcon color="primary" />
                          <Typography variant="h6" noWrap>
                            {resume.company_name}
                          </Typography>
                        </Stack>

                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <WorkIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {resume.job_title}
                            </Typography>
                          </Stack>

                          {resume.ats_score && (
                            <Stack spacing={1}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <AssessmentIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                  ATS Score
                                </Typography>
                              </Stack>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={resume.ats_score * 100}
                                  sx={{ flexGrow: 1 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {(resume.ats_score * 100).toFixed(1)}%
                                </Typography>
                              </Box>
                            </Stack>
                          )}

                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Created: {new Date(resume.created_at).toLocaleDateString()}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </CardContent>

                    <Divider />

                    <Box sx={{ p: 2 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleDownload(resume.resume_url, resume.company_name)}
                        startIcon={<DownloadIcon />}
                      >
                        Download Resume
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResumeHistory; 